/**
 * Hijacks the product form to add items via the Cart AJAX API.
 * On success, dispatches cart:updated and cart:open events to
 * update the cart icon and open the cart drawer.
 * Announces result to screen readers via aria-live region.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  #announce(message) {
    const status = document.getElementById('cart-status');
    if (status) {
      status.textContent = message;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    const originalText = this.submitButton.textContent;
    this.submitButton.textContent = 'Adding...';

    try {
      const formData = new FormData(this.form);
      const mainItem = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity') || 1),
        properties: {},
      };

      // Forward any properties[*] form fields into the bulk-add payload.
      for (const [key, value] of formData.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value) mainItem.properties[m[1]] = value;
      }
      if (Object.keys(mainItem.properties).length === 0) delete mainItem.properties;

      const items = [mainItem];

      // Detect the gift wrap toggle. The checkbox lives outside the <form>'s
      // submitted data on purpose so its value isn't passed to the main item.
      const wrapToggle = this.querySelector('[data-gift-wrap-toggle]');
      if (wrapToggle && wrapToggle.checked) {
        const wrapVariantId = Number(wrapToggle.dataset.giftWrapVariantId);
        if (wrapVariantId) {
          items.push({
            id: wrapVariantId,
            quantity: 1,
            properties: {
              _gift_wrap: 'true',
              _pairs_with: String(mainItem.id),
            },
          });
        }
      }

      const giftBoxToggle = this.querySelector('[data-gift-box-toggle]');
      if (giftBoxToggle && giftBoxToggle.checked) {
        const giftBoxVariantId = Number(giftBoxToggle.dataset.giftBoxVariantId);
        if (giftBoxVariantId) {
          items.push({
            id: giftBoxVariantId,
            quantity: 1,
            properties: {
              _gift_box: 'true',
              _pairs_with: String(mainItem.id),
            },
          });
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } })
      );
      document.dispatchEvent(new CustomEvent('cart:open'));

      this.#announce(`Added to cart. Cart now has ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.`);
    } catch (error) {
      console.error('Add to cart error:', error);
      this.submitButton.textContent = 'Error — try again';
      this.#announce('Failed to add item to cart. Please try again.');
      setTimeout(() => {
        this.submitButton.textContent = originalText.trim();
      }, 2000);
      this.submitButton.removeAttribute('aria-busy');
      this.submitButton.disabled = false;
      return;
    }

    this.submitButton.disabled = false;
    this.submitButton.removeAttribute('aria-busy');
    this.submitButton.textContent = originalText.trim();
  }
}

customElements.define('product-form', ProductForm);

export { ProductForm };
